import { action, computed, observable } from 'mobx';

import {
    defaultImpuestosPredialCuentasAcumulados,
    defaultImpuestosPredialCuentasDePagos,
    defaultImpuestosPredialCuentasExistente,
    impuestosPredialCuentasExistenteKeys,
    impuestosPredialCuentasExistenteTiposDePredioByKey
} from './constants';
import { registroImpuestosPredialStore } from './registroImpuestosPredialStore';
import { setValueByPath } from './utils';

import {
    ImpuestosPredialCuentasAcumulados,
    ImpuestosPredialCuentasAcumuladosRecord,
    ImpuestosPredialCuentasDePagos,
    ImpuestosPredialCuentasDePagosRecord,
    ImpuestosPredialCuentasExistente,
    ImpuestosPredialCuentasExistenteRecord,
    RegistroImpuestosEstado
} from '../models';

export class ImpuestosPredialCuentasStore {
    @observable
    isSaving = false;

    @computed
    get isLoading() {
        return registroImpuestosPredialStore.isLoading;
    }

    @computed
    get ejercicio(): number {
        return registroImpuestosPredialStore.apertura.ejercicio;
    }

    @computed
    get cuentasExistenteRecords(): ImpuestosPredialCuentasExistenteRecord[] {
        return [
            ...this.resolveCuentasExistenteRecords(registroImpuestosPredialStore.cuentas),
            ...this.resolveCuentasExistenteTotalRecords(registroImpuestosPredialStore.cuentas)
        ];
    }

    @computed
    get cuentasDePagosRecords(): ImpuestosPredialCuentasDePagosRecord[] {
        return this.resolveCuentasDePagosRecords(registroImpuestosPredialStore.cuentas);
    }

    @computed
    get cuentasDePagosAcumuladosRecords(): ImpuestosPredialCuentasAcumuladosRecord[] {
        return this.resolveCuentasAcumuladosRecords(registroImpuestosPredialStore.cuentas);
    }

    @action
    setCuentasValue = (path: string, value: number) => {
        setValueByPath(registroImpuestosPredialStore.cuentas, path, value);
        registroImpuestosPredialStore.markDirty();
    };

    @action
    saveCuentas = async () => {
        this.isSaving = true;
        const { registroImpuestos } = registroImpuestosPredialStore;
        const { estado, id } = registroImpuestos;
        const subform = registroImpuestos.cuentas;
        const formatoNum = 2;

        if (estado === RegistroImpuestosEstado.SinCaptura || id === 0) {
            await registroImpuestosPredialStore.getNewForm();
        }

        const result = await registroImpuestosPredialStore.saveForm(subform, formatoNum);
        registroImpuestosPredialStore.unmarkDirty();

        this.isSaving = false;
        if (result === 'success') {
            registroImpuestosPredialStore.registroImpuestos.cuentas.isSaved = true;
        }
    };

    private resolveCuentasExistenteRecords = (
        numeroDeCuentasByKey: ImpuestosPredialCuentasExistente
    ): ImpuestosPredialCuentasExistenteRecord[] =>
        impuestosPredialCuentasExistenteKeys.map(key => ({
            key,
            tipoDePredio: impuestosPredialCuentasExistenteTiposDePredioByKey[key],
            numeroDeCuentas: numeroDeCuentasByKey[key]
        }));

    private resolveCuentasExistenteTotalRecords = ({
        numeroDeCuentasUrbano,
        numeroDeCuentasRustico
    }: ImpuestosPredialCuentasExistente): ImpuestosPredialCuentasExistenteRecord[] => [
        {
            key: 'total',
            tipoDePredio: 'TOTAL',
            numeroDeCuentas: numeroDeCuentasUrbano + numeroDeCuentasRustico
        }
    ];

    private resolveCuentasDePagosRecords = ({
        numeroDePagosEjercicio,
        numeroDePagosEjercicioAnterior,
        numeroDePagosEjercicioMixto
    }: ImpuestosPredialCuentasDePagos): ImpuestosPredialCuentasDePagosRecord[] => [
        {
            key: 'cuentasDePagos',
            numeroDePagosEjercicio,
            numeroDePagosEjercicioAnterior,
            numeroDePagosEjercicioMixto,
            numeroDePagosTotal:
                numeroDePagosEjercicio +
                numeroDePagosEjercicioAnterior +
                numeroDePagosEjercicioMixto
        }
    ];

    private resolveCuentasAcumuladosRecords = ({
        numeroDePagosAcumuladoEjercicio,
        numeroDePagosAcumuladoEjercicioAnterior,
        numeroDePagosAcumuladoEjercicioMixto
    }: ImpuestosPredialCuentasAcumulados): ImpuestosPredialCuentasAcumuladosRecord[] => [
        {
            key: 'cuentasDePagosAcumulados',
            numeroDePagosAcumuladoEjercicio,
            numeroDePagosAcumuladoEjercicioAnterior,
            numeroDePagosAcumuladoEjercicioMixto,
            numeroDePagosAcumuladoTotal:
                numeroDePagosAcumuladoEjercicio +
                numeroDePagosAcumuladoEjercicioAnterior +
                numeroDePagosAcumuladoEjercicioMixto
        }
    ];
}

export const impuestosPredialCuentasStore = new ImpuestosPredialCuentasStore();
